import { useState, useEffect, useRef } from 'react';
import { Link } from '@remix-run/react';
import { X, Search, Clock, ChevronRight } from 'lucide-react';
import { NEWS_CATEGORY_MAPPING } from '~/constants/news';
import { formatDate } from '~/lib/utils';

// 광고 소스 타입 정의
type AdSource = 'default' | 'finance' | 'realestate';

// 검색 결과 아이템 타입
export interface NewsSearchItem {
  id: string;
  title: string;
  summary?: string;
  thumbnail_url?: string;
  category: string;
  view_count: number;
  created_at: string;
}

// 검색 모달 Props
interface NewsSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchResults: NewsSearchItem[];
  searchQuery: string;
  isLoading: boolean;
  adSource?: AdSource;
}

// 카테고리별 배지 스타일
const getCategoryBadgeStyle = (category: string) => {
  switch (category) {
    case 'firm_news':
      return 'bg-blue-50 text-blue-700';
    case 'case_analysis':
      return 'bg-purple-50 text-purple-700';
    case 'legal_trends':
      return 'bg-amber-50 text-amber-700';
    case 'legal_column':
      return 'bg-emerald-50 text-emerald-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

// 광고 소스별 색상 스타일
const getAdSourceColors = (adSource: AdSource = 'default') => {
  switch (adSource) {
    case 'finance':
      return {
        accent: '#1f2937',  // 짙은 회색
        hover: '#111827'    // 더 짙은 회색 (거의 검정)
      };
    case 'realestate':
      return {
        accent: '#000000',  // 순수 검정
        hover: '#1f2937'    // 짙은 회색
      };
    default:
      return {
        accent: '#b98e6a',  // 원래 브라운
        hover: '#8b6b4f'    // 더 진한 브라운
      };
  }
};

export function NewsSearchModal({ 
  isOpen, 
  onClose, 
  searchResults, 
  searchQuery,
  isLoading,
  adSource = 'default'
}: NewsSearchModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const colors = getAdSourceColors(adSource);
  
  // ESC 키를 누르면 모달 닫기
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    // 모달이 열려있을 때만 이벤트 리스너 등록
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);
  
  // 모달 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    
    // 모달이 열려있을 때만 이벤트 리스너 등록
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);
  
  // 스크롤 방지 및 floating 버튼 숨기기
  useEffect(() => {
    const floatingButtons = document.querySelector('.fixed.bottom-6.right-6');
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (floatingButtons) {
        (floatingButtons as HTMLElement).style.display = 'none';
      }
    } else {
      document.body.style.overflow = '';
      if (floatingButtons) {
        (floatingButtons as HTMLElement).style.display = '';
      }
    }
    
    return () => {
      document.body.style.overflow = '';
      if (floatingButtons) {
        (floatingButtons as HTMLElement).style.display = '';
      }
    };
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div 
        ref={modalRef}
        className="w-full max-w-3xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh] animate-fadeIn"
      >
        {/* 모달 헤더 */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900 flex items-center">
            <Search className="w-5 h-5 mr-2" style={{ color: colors.accent }} />
            검색 결과
          </h3>
          <button 
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* 모달 본문 */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* 검색어 표시 */}
          <div className="mb-6">
            <p className="text-gray-600">
              <span className="text-gray-900 font-medium">"{searchQuery}"</span>에 대한 검색 결과
              {!isLoading && <span className="text-gray-500 ml-2">({searchResults.length}건)</span>}
            </p>
          </div>
          
          {/* 로딩 상태 */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative w-16 h-16 mb-4">
                <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
                <div className="absolute inset-0 border-4 rounded-full border-t-transparent animate-spin" 
                     style={{ borderColor: `${colors.accent} transparent transparent transparent` }}></div>
              </div>
              <p className="text-gray-600">검색 중입니다...</p>
            </div>
          )}
          
          {/* 검색 결과 없음 */}
          {!isLoading && searchResults.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">검색 결과가 없습니다</h4>
              <p className="text-gray-600">다른 검색어로 다시 시도해보세요.</p>
            </div>
          )}
          
          {/* 검색 결과 목록 */}
          {!isLoading && searchResults.length > 0 && (
            <div className="space-y-4">
              {searchResults.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  prefetch="intent"
                  onClick={onClose}
                  className="block bg-white border border-gray-200 rounded-lg p-4 transition-all duration-200"
                  style={{ 
                    ['&:hover' as any]: { 
                      borderColor: `${colors.accent}50`,
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    } 
                  }}
                >
                  <div className="flex items-start gap-4">
                    {/* 썸네일 영역 */}
                    {item.thumbnail_url ? (
                      <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
                        <img 
                          src={item.thumbnail_url} 
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-20 h-20 bg-gray-100 rounded-md flex items-center justify-center flex-shrink-0">
                        <Search className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    
                    {/* 콘텐츠 영역 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs px-2 py-1 rounded-full ${getCategoryBadgeStyle(item.category)}`}>
                          {NEWS_CATEGORY_MAPPING[item.category]}
                        </span>
                        <div className="flex items-center text-xs text-gray-500">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDate(item.created_at)}
                        </div>
                      </div>
                      
                      <h4 className="text-base font-medium text-gray-900 mb-1 truncate">
                        {item.title}
                      </h4>
                      
                      {item.summary && (
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {item.summary}
                        </p>
                      )}
                    </div>
                    
                    {/* 화살표 아이콘 */}
                    <div style={{ color: colors.accent }} className="self-center">
                      <ChevronRight className="w-5 h-5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
        
        {/* 모달 푸터 */}
        <div className="bg-gray-50 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-white rounded-md transition-colors"
            style={{ backgroundColor: colors.accent, ['&:hover' as any]: { backgroundColor: colors.hover } }}
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
} 